import React, { useState } from 'react';
import { ethers } from 'ethers';
import { toast } from './Toast';
import '../styles/LockForm.css';

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function balanceOf(address account) view returns (uint256)",
  "function decimals() view returns (uint8)",
];

function LockForm({ contract, signer, stablecoins, onLockCreated, disabled }) {
  const [selectedToken, setSelectedToken] = useState('USDC');
  const [amount, setAmount] = useState('');
  const [duration, setDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('days');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [savingsGoal, setSavingsGoal] = useState('');
  const [lockMode, setLockMode] = useState('duration'); // 'duration', 'amount', or 'flexible'
  const [lockName, setLockName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [txStep, setTxStep] = useState(null); // null | 'approve-wallet' | 'approve-confirming' | 'lock-wallet' | 'lock-confirming' | 'done'
  const [balance, setBalance] = useState('');
  const [platformFee, setPlatformFee] = useState('0');
  const [feeConfiguration, setFeeConfiguration] = useState(null);

  const getDurationInSeconds = () => {
    const durationNum = parseInt(duration) || 0;
    switch (durationUnit) {
      case 'hours':
        return durationNum * 3600;
      case 'days':
        return durationNum * 86400;
      case 'weeks':
        return durationNum * 604800;
      case 'months':
        return durationNum * 2592000; // 30 days
      default:
        return 0;
    }
  };

  // Fetch fee configuration when contract loads
  React.useEffect(() => {
    if (contract) {
      contract.getFeeConfiguration()
        .then(result => {
          setFeeConfiguration({
            percentageBP: result[0],
            fixedAmount: result[1]
          });
        })
        .catch(err => console.error('Error fetching fee config:', err));
    }
  }, [contract]);

  // Calculate platform fee when amount changes
  React.useEffect(() => {
    if (amount && contract && feeConfiguration) {
      try {
        const amountNum = parseFloat(amount);
        if (amountNum > 0) {
          const tokenDecimals = stablecoins[selectedToken].decimals;
          const amountInWei = ethers.parseUnits(amount, tokenDecimals);
          
          contract.calculatePlatformFee(amountInWei)
            .then(feeInWei => {
              setPlatformFee(ethers.formatUnits(feeInWei, tokenDecimals));
            })
            .catch(err => console.error('Error calculating fee:', err));
        }
      } catch (error) {
        console.error('Error in fee calculation:', error);
      }
    }
  }, [amount, selectedToken, contract, feeConfiguration]);

  const handleTokenChange = async (token) => {
    setSelectedToken(token);
    
    if (signer) {
      try {
        const tokenAddress = stablecoins[token].address;
        const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
        const userBalance = await tokenContract.balanceOf(await signer.getAddress());
        const decimals = await tokenContract.decimals();
        setBalance(ethers.formatUnits(userBalance, decimals));
      } catch (error) {
        console.error('Error fetching balance:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contract || !signer) {
      toast.error('Wallet not connected');
      return;
    }

    if (!amount) {
      toast.warning('Please enter an amount');
      return;
    }

    // Validation based on lock mode
    if (lockMode === 'duration' || lockMode === 'flexible') {
      if (!duration) {
        toast.warning('Please enter a duration');
        return;
      }
    } else if (lockMode === 'amount') {
      if (!savingsGoal || parseFloat(savingsGoal) <= 0) {
        toast.warning('Please enter a valid savings goal amount');
        return;
      }
      if (parseFloat(savingsGoal) < parseFloat(amount)) {
        toast.warning('Savings goal must be greater than or equal to the lock amount');
        return;
      }
    }

    const amountNum = parseFloat(amount);
    if (amountNum <= 0) {
      toast.warning('Amount must be greater than 0');
      return;
    }

    try {
      setLoading(true);

      const tokenData = stablecoins[selectedToken];
      const tokenAddress = tokenData.address;
      const tokenDecimals = tokenData.decimals;

      // Calculate amounts
      const amountInWei = ethers.parseUnits(amount, tokenDecimals);
      const goalInWei = lockMode === 'amount' ? ethers.parseUnits(savingsGoal, tokenDecimals) : 0;
      
      // Calculate duration in seconds
      let durationInSeconds = 0;
      if (lockMode === 'duration' || lockMode === 'flexible') {
        durationInSeconds = getDurationInSeconds();
      } else if (lockMode === 'amount') {
        // For amount mode, set a far-future unlock time (goal controls withdrawal)
        durationInSeconds = 1000 * 365 * 86400; // 1000 years (effectively no time limit)
      }
      
      const unlockTime = Math.floor(Date.now() / 1000) + durationInSeconds;

      // Step 1: Approve token spending (include fee)
      setTxStep('approve-wallet');
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      const totalCost = amountInWei + ethers.parseUnits(platformFee, tokenDecimals);
      const approveTx = await tokenContract.approve(
        await contract.getAddress(),
        totalCost
      );
      setTxStep('approve-confirming');
      await approveTx.wait();

      // Step 2: Create lock
      setTxStep('lock-wallet');
      const beneficiary = recipientAddress || await signer.getAddress();
      
      const lockTx = await contract.lockTokens(
        tokenAddress,
        amountInWei,
        unlockTime,
        beneficiary,
        goalInWei,
        lockMode === 'flexible',
        lockName
      );
      
      setTxStep('lock-confirming');
      await lockTx.wait();
      setTxStep('done');

      const goalMsg = lockMode === 'amount' ? ` with savings goal of ${savingsGoal} ${selectedToken}` : '';
      const flexMsg = lockMode === 'flexible' ? ' (flexible - emergency withdraw enabled)' : '';
      toast.success(`Successfully locked ${amount} ${selectedToken}${goalMsg}${flexMsg}`);
      
      // Reset form
      setAmount('');
      setDuration('');
      setRecipientAddress('');
      setSavingsGoal('');
      setLockMode('duration');
      setLockName('');
      
      onLockCreated();
    } catch (error) {
      console.error('Error creating lock:', error);
      toast.error('Failed to create lock: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
      setTimeout(() => setTxStep(null), 2000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="lock-form">
      <div className="form-group">
        <label>Lock Mode</label>
        <div className="lock-mode-selector">
          <div
            className={`mode-card ${lockMode === 'duration' ? 'selected' : ''}`}
            onClick={() => !(loading || disabled) && setLockMode('duration')}
          >
            <span className="mode-icon">🔒</span>
            <div className="mode-text">
              <strong>Hard Lock</strong>
              <span>No early withdrawal</span>
            </div>
          </div>
          <div
            className={`mode-card ${lockMode === 'amount' ? 'selected' : ''}`}
            onClick={() => !(loading || disabled) && setLockMode('amount')}
          >
            <span className="mode-icon">🎯</span>
            <div className="mode-text">
              <strong>Savings Goal</strong>
              <span>Withdraw at target</span>
            </div>
          </div>
          <div
            className={`mode-card ${lockMode === 'flexible' ? 'selected' : ''}`}
            onClick={() => !(loading || disabled) && setLockMode('flexible')}
          >
            <span className="mode-icon">🔓</span>
            <div className="mode-text">
              <strong>Flexible</strong>
              <span>10% penalty to exit early</span>
            </div>
          </div>
        </div>
      </div>

      <div className="form-row-token-amount">
        <div className="form-group form-group-token">
          <label htmlFor="token">Token</label>
          <select
            id="token"
            value={selectedToken}
            onChange={(e) => handleTokenChange(e.target.value)}
            disabled={loading || disabled}
          >
            {Object.keys(stablecoins).map((token) => (
              <option key={token} value={token}>
                {token}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group form-group-amount">
          <label htmlFor="amount">Amount</label>
          <input
            id="amount"
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="0.01"
            min="0"
            disabled={loading || disabled}
          />
        </div>
      </div>
      {balance && <small className="balance-hint">Balance: {balance} {selectedToken}</small>}

      {(lockMode === 'duration' || lockMode === 'flexible') && (
        <div className="form-group-row">
          <div className="form-group">
            <label htmlFor="duration">Duration</label>
            <input
              id="duration"
              type="number"
              placeholder="e.g. 30"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              min="1"
              disabled={loading || disabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="unit">Unit</label>
            <select
              id="unit"
              value={durationUnit}
              onChange={(e) => setDurationUnit(e.target.value)}
              disabled={loading || disabled}
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
          </div>
        </div>
      )}

      {lockMode === 'amount' && (
        <div className="form-group">
          <label htmlFor="goal">Target Amount</label>
          <input
            id="goal"
            type="number"
            placeholder="e.g. 500"
            value={savingsGoal}
            onChange={(e) => setSavingsGoal(e.target.value)}
            step="0.01"
            min={amount || "0"}
            disabled={loading || disabled}
          />
          <small>Withdraw unlocks once this amount is saved</small>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="lockName">Lock Name <span className="optional-label">(optional)</span></label>
        <input
          id="lockName"
          type="text"
          placeholder="e.g. Vacation Fund, Emergency Savings"
          value={lockName}
          onChange={(e) => setLockName(e.target.value)}
          maxLength={64}
          disabled={loading || disabled}
        />
      </div>

      <div className="advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
        {showAdvanced ? '▾' : '▸'} Send to another wallet
      </div>

      {showAdvanced && (
        <div className="form-group">
          <input
            id="recipient"
            type="text"
            placeholder="0x... recipient address"
            value={recipientAddress}
            onChange={(e) => setRecipientAddress(e.target.value)}
            disabled={loading || disabled}
          />
        </div>
      )}

      {amount && platformFee !== '0' && (
        <div className="fee-inline">
          <span>Fee: {platformFee} {selectedToken} ({feeConfiguration ? (Number(feeConfiguration.percentageBP) / 100) : 0}%)</span>
          <span>Total: {(parseFloat(amount) + parseFloat(platformFee)).toFixed(6)} {selectedToken}</span>
        </div>
      )}

      {txStep && (
        <div className="tx-progress">
          <div className={`tx-step ${txStep === 'approve-wallet' || txStep === 'approve-confirming' ? 'active' : ['lock-wallet','lock-confirming','done'].includes(txStep) ? 'done' : ''}`}>
            <div className="tx-step-indicator">
              {['lock-wallet','lock-confirming','done'].includes(txStep) 
                ? <span className="checkmark">✓</span> 
                : <span className="spinner" />}
            </div>
            <div className="tx-step-text">
              <strong>Approve</strong>
              <span>{txStep === 'approve-wallet' ? 'Confirm in wallet...' : txStep === 'approve-confirming' ? 'Confirming on chain...' : 'Approved'}</span>
            </div>
          </div>
          <div className={`tx-step-line ${['lock-wallet','lock-confirming','done'].includes(txStep) ? 'filled' : txStep === 'approve-confirming' ? 'filling' : ''}`}>
            <div className="tx-step-line-fill" />
          </div>
          <div className={`tx-step ${txStep === 'lock-wallet' || txStep === 'lock-confirming' ? 'active' : txStep === 'done' ? 'done' : ''}`}>
            <div className="tx-step-indicator">
              {txStep === 'done' 
                ? <span className="checkmark">✓</span> 
                : txStep === 'lock-wallet' || txStep === 'lock-confirming' 
                  ? <span className="spinner" /> 
                  : <span className="step-num">2</span>}
            </div>
            <div className="tx-step-text">
              <strong>Lock</strong>
              <span>{txStep === 'lock-wallet' ? 'Confirm in wallet...' : txStep === 'lock-confirming' ? 'Confirming on chain...' : txStep === 'done' ? 'Complete!' : 'Waiting...'}</span>
            </div>
          </div>
          {txStep === 'done' && <div className="tx-success-glow" />}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || disabled}
        className="btn-submit"
      >
        {loading ? (txStep === 'done' ? '✓ Locked!' : 'Processing...') : 'Lock Tokens'}
      </button>
    </form>
  );
}

export default LockForm;
